import { apiConsumer } from ".";
import {
  Product,
  SalesHistoryQueries,
  SalesHistoryResponse,
  saleStatus,
} from "../interfaces/sales";
import {
  CancelSaleProductRestock,
  CompleteSaleBody,
  PartialCancelSaleBody,
  SalesDetail,
  salesListPagination,
  salesQueries,
} from "../interfaces/sales";
import {
  getSellerNameWithAppOrderType,
  getTranslatedSaleStatus,
} from "../translates/sales/sales";
import { getBodyWithQueries, getUrlWithqueries } from "../utils/buildUrlWithQueries";
import { formatDate } from "../utils/dates";
import { isTipAdjustmentSale } from "../utils/sales";

export const getAllSalesService = async (
  queries?: salesQueries,
): Promise<salesListPagination> => {
  const body = getBodyWithQueries(queries);
  const { data } = await apiConsumer({
    endpoint: "/sales/list",
    method: "POST",
    body,
  });

  const formatedData = {
    ...data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: data.data.map((item: any) => {
      return {
        ...item,
        // On-screen Total column value including the app service fee (commission); `total` stays fee-free
        // so exports keep their original amounts.
        total_with_fee: Number(item.total ?? 0) + Number(item.app_service_fee ?? 0),
        seller_name: getSellerNameWithAppOrderType(
          item.seller.name,
          item.app_order_type,
        ),
        event_name: item.event.name,
        concession_name: item.concession.name,
        sold_at: formatDate(item.sold_at),
        translated_status: getTranslatedSaleStatus(item.status),
        // Tag tip-reassignment sales in the list, based on the API-provided `type`
        order_type_tag: isTipAdjustmentSale(item) ? "Ajuste de propina" : "",
      };
    }),
  };

  return formatedData;
};

export const getAllDetailedProductsSalesService = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queries?: any,
): Promise<salesListPagination> => {
  const urlWithQueries = getUrlWithqueries("/sales/detailed/products", queries);

  const { data } = await apiConsumer({
    endpoint: urlWithQueries,
    method: "GET",
  });

  const formatedData = {
    ...data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: data.data.map((item: any) => {
      return {
        ...item,
        sold_at: item.sale?.sold_at ? formatDate(item.sale?.sold_at) : "-",
        sale_code: item.sale?.code || "-",
        price_type:
          item.discountAndPromotion?.discount_name || "Precio regular",
      };
    }),
  };

  return formatedData;
};

export const getAllDetailedProductsSalesServicePost = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queries?: any,
): Promise<salesListPagination> => {
  const body = getBodyWithQueries(queries);

  const { data } = await apiConsumer({
    endpoint: "/sales/detailed/products",
    method: "POST",
    body,
  });

  const formatedData = {
    ...data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: data.data.map((item: any) => {
      return {
        ...item,
        sold_at: item.sale?.sold_at ? formatDate(item.sale?.sold_at) : "-",
        sale_code: item.sale?.code || "-",
        price_type:
          item.discountAndPromotion?.discount_name || "Precio regular",
      };
    }),
  };

  return formatedData;
};

export const getAllDetailedPaymentsSalesService = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queries?: any,
): Promise<salesListPagination> => {
  const urlWithQueries = getUrlWithqueries("/sales/detailed/payments", queries);
  const { data } = await apiConsumer({
    endpoint: urlWithQueries,
    method: "GET",
  });

  const formatedData = {
    ...data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: data.data.map((item: any) => {
      return {
        ...item,
        paid_at: formatDate(item.paid_at),
        sold_at: formatDate(item.sale?.sold_at),
        subtotal_sale: item.sale?.subtotal,
        tip_sale: item.sale?.tip,
        total_sale: item.sale?.total,
        total_products: item.sale?.total_products,
        status: saleStatus[item.sale?.status],
        sale_code: item.sale?.code || "-",
        code: item.code || "-",
        previous_balance: item.nfc_transaction?.previous_balance || "-",
        post_balance: item.nfc_transaction?.post_balance || "-",
        card_number: item.card_transaction
          ? item.card_transaction?.bin_card +
            "******" +
            item.card_transaction?.last_four
          : item.nfc_transaction?.serial_number || "-",
        card_brand: item.card_transaction?.brand_card || "YOSHI",
        card_bank: item.card_transaction?.bank_card || "YOSHI",
        card_type: item.card_transaction?.type_card || "YOSHI",
        entry_mode: item.card_transaction?.entry_mode || "CONTACTLESS",
        authorization_code: item.card_transaction?.authorization || "-",
        terminal: item.card_transaction?.terminal || "-",
        merchant: item.card_transaction?.merchant || "-",
      };
    }),
  };

  return formatedData;
};

export const getAllDetailedPaymentsSalesServicePost = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queries?: any,
): Promise<salesListPagination> => {
  const body = getBodyWithQueries(queries);
  const { data } = await apiConsumer({
    endpoint: "/sales/detailed/payments",
    method: "POST",
    body,
  });

  const formatedData = {
    ...data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: data.data.map((item: any) => {
      return {
        ...item,
        paid_at: formatDate(item.paid_at),
        sold_at: formatDate(item.sale?.sold_at),
        subtotal_sale: item.sale?.subtotal,
        tip_sale: item.sale?.tip,
        total_sale: item.sale?.total,
        total_products: item.sale?.total_products,
        status: saleStatus[item.sale?.status],
        sale_code: item.sale?.code || "-",
        code: item.code || "-",
        previous_balance: item.nfc_transaction?.previous_balance || "-",
        post_balance: item.nfc_transaction?.post_balance || "-",
        card_number: item.card_transaction
          ? item.card_transaction?.bin_card +
            "******" +
            item.card_transaction?.last_four
          : item.nfc_transaction?.serial_number || "-",
        card_brand: item.card_transaction?.brand_card || "YOSHI",
        card_bank: item.card_transaction?.bank_card || "YOSHI",
        card_type: item.card_transaction?.type_card || "YOSHI",
        entry_mode: item.card_transaction?.entry_mode || "CONTACTLESS",
        authorization_code: item.card_transaction?.authorization || "-",
        terminal: item.card_transaction?.terminal || "-",
        merchant: item.card_transaction?.merchant || "-",
      };
    }),
  };

  return formatedData;
};

export const getSaleDetailService = async (
  uuid: string,
): Promise<SalesDetail> => {
  const { data } = await apiConsumer({
    method: "GET",
    endpoint: `/sales/${uuid}`,
  });

  return {
    ...data,
    products: data.products.map((product: Product) => {
      const isDiscount = product.price !== product.base_price;
      return {
        ...product,
        discountName:
          product.token_redemption ? "Canje Tokens" :
          product.discount?.name ||
          product.promotion?.name ||
          product.combo_name ||
          (isDiscount ? "Descuento" : "Precio regular"),
      };
    }),
  };
};

export const completeSaleService = async (
  uuid: string,
  body?: CompleteSaleBody,
): Promise<void> => {
  const payload: CompleteSaleBody = { payments: body?.payments ?? [] };
  await apiConsumer({
    method: "POST",
    endpoint: `/sales/${uuid}/complete`,
    body: payload,
  });

  return Promise.resolve();
};

export const refundSaleService = async (
  uuid: string,
  transaction_id: string,
  phoneNumber?: string,
  comment?: string,
  products?: CancelSaleProductRestock[],
): Promise<any> => {
  const { data } = await apiConsumer({
    method: "POST",
    endpoint: `/sales/${uuid}/cancel`,
    body: {
      transaction_id,
      comment: comment || '',
      ...(phoneNumber ? { phone_number: phoneNumber } : {}),
      ...(products && products.length > 0 ? { products } : {}),
    },
  });

  return data;
};

export const getSalesHistoryService = async (
  queries?: SalesHistoryQueries,
): Promise<SalesHistoryResponse> => {
  const urlWithQueries = getUrlWithqueries("/sales/overview", queries);
  const { data } = await apiConsumer({
    method: "GET",
    endpoint: urlWithQueries,
  });

  return {
    data: data,
  };
};

export const getSalesHistoryServicePost = async (
  queries?: SalesHistoryQueries,
): Promise<SalesHistoryResponse> => {
  const body = getBodyWithQueries(queries);
  const { data } = await apiConsumer({
    method: "POST",
    endpoint: "/sales/overview",
    body,
  });

  return {
    data: data,
  };
};

export const getProductsByPaymentMethodService = async (queries?: any): Promise<any> => {
  const body = getBodyWithQueries(queries);
  const { data } = await apiConsumer({
    method: "POST",
    endpoint: "/sales/summary/products-by-payment",
    body,
  });
  return data;
};

export const partialCancelSaleService = async (
  uuid: string,
  body: PartialCancelSaleBody,
): Promise<any> => {
  const { data } = await apiConsumer({
    method: "POST",
    endpoint: `/sales/${uuid}/partial-cancel`,
    body,
  });

  return data;
};

export const getSellerPaymentReportService = async (queries?: any): Promise<any> => {
  const urlWithQueries = getUrlWithqueries("/sales/sellers/payment", queries);
  const { data } = await apiConsumer({
    method: "GET",
    endpoint: urlWithQueries,
  });
  return data;
};

export const getSellerPaymentReportServicePost = async (queries?: any): Promise<any> => {
  const body = getBodyWithQueries(queries);
  const { data } = await apiConsumer({
    method: "POST",
    endpoint: "/sales/sellers/payment",
    body,
  });
  return data;
};
